/**
 * Admin Module Metadata — Derived from Canonical Sources
 *
 * This file is the SINGLE shared utility for all admin-facing module metadata.
 * It derives everything from src/content/modules.ts and src/content/moduleColors.ts.
 *
 * Do NOT create local module dictionaries elsewhere in the admin portal.
 * Import from here instead.
 */

import {
  MODULES,
  getModuleShortName,
  MODULE_ID_TO_CODE,
  MODULE_CODE_TO_ID,
} from '../../../content/modules'
import {
  getModuleColors,
  getModuleHexColor,
} from '../../../content/moduleColors'
import type { ModuleId } from '../../../content/types'

// Re-export for convenience
export { MODULE_CODE_TO_ID } from '../../../content/modules'

// ─── Types ───────────────────────────────────────────────────

export interface AdminModuleMeta {
  /** Short code: 'GET', 'REK', etc. */
  code: string
  /** Typed module ID: 'Getalbegrip', 'Basisrekenen', etc. */
  moduleId: ModuleId
  /** Display name from canonical source (e.g. 'Getallen') */
  name: string
  /** Compact name for heatmaps etc. (e.g. 'Get.') */
  shortName: string
  /** Tailwind bg class (e.g. 'bg-indigo-500') */
  bgClass: string
  /** Tailwind text class for dark variant (e.g. 'text-indigo-700') */
  textClass: string
  /** Hex color for SVG strokes (e.g. '#6366F1') */
  hexColor: string
  /** Alias for bgClass — used by SUBJECT_META consumers */
  color: string
}

// ─── Build from canonical source ─────────────────────────────

function buildAdminModuleMetadata(): Record<string, AdminModuleMeta> {
  const result: Record<string, AdminModuleMeta> = {}
  for (const mod of MODULES) {
    const code = MODULE_ID_TO_CODE[mod.id]
    if (!code) continue
    const colors = getModuleColors(mod.color)
    const textClass = mod.color.replace('500', '700')
    result[code] = {
      code,
      moduleId: mod.id,
      name: mod.title,
      shortName: getModuleShortName(mod.id),
      bgClass: colors.bg,
      textClass,
      hexColor: colors.ring,
      color: colors.bg,
    }
  }
  return result
}

/** Single source of truth for admin module metadata, keyed by short code */
export const ADMIN_MODULE_META = buildAdminModuleMetadata()

/** Get display label for a short code (e.g. 'GET' → 'Getallen') */
export function getModuleLabel(code: string): string {
  return ADMIN_MODULE_META[code]?.name ?? code
}

/** All valid module short codes */
export const ALL_MODULE_CODES = Object.keys(ADMIN_MODULE_META)

/**
 * Legacy TGL support: resolve legacy code to current code.
 * Old mastery data may still contain TGL-prefixed familyIds.
 */
export function resolveModuleCode(code: string): string {
  if (code === 'TGL') return 'TIJD'
  return code
}
